/**
 * Authentication Token Service
 * Handles storage and retrieval of JWT tokens in localStorage
 */

const ACCESS_TOKEN_KEY = 'chat_checkout_access_token';
const REFRESH_TOKEN_KEY = 'chat_checkout_refresh_token';

/**
 * Get the current access token from localStorage
 */
export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to get access token from localStorage:', error);
    return null;
  }
}

/**
 * Get the current refresh token from localStorage
 */
export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to get refresh token from localStorage:', error);
    return null;
  }
}

/**
 * Store both access and refresh tokens in localStorage
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } catch (error) {
    console.error('Failed to store tokens in localStorage:', error);
  }
}

/**
 * Remove all tokens from localStorage
 */
export function clearTokens(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to clear tokens from localStorage:', error);
  }
}

/**
 * Check if the user is authenticated (has a valid access token)
 * Note: This only checks for token existence, not validity
 */
export function isAuthenticated(): boolean {
  const token = getAccessToken();
  if (!token) {
    return false;
  }

  // Optionally check if token is expired by decoding JWT
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    return Date.now() < expirationTime;
  } catch (error) {
    // If we can't decode the token, assume it's invalid
    return false;
  }
}

/**
 * Get the token expiration time in milliseconds
 */
export function getTokenExpiration(): number | null {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  } catch (error) {
    return null;
  }
}

/**
 * Check if the access token will expire soon (within the given threshold)
 * @param thresholdMs - Time in milliseconds before expiration to consider "soon"
 */
export function isTokenExpiringSoon(thresholdMs: number = 60000): boolean {
  const expiration = getTokenExpiration();
  if (!expiration) {
    return true;
  }

  return Date.now() + thresholdMs >= expiration;
}

export default {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isAuthenticated,
  getTokenExpiration,
  isTokenExpiringSoon,
};
